import React, { useMemo } from 'react';

import { UILoader } from '@futrli/ui-components';

import { CATEGORY_NAME_INDEXES } from '../../../../../core/constants';
import {
  ConsolidatedForecastReportTab,
  PredictionsAccountCategories,
  PredictionsSubCategories
} from '../../../../../core/types';
import { ForecastTree, UKForecastLayoutItem } from '../../types';
import { ForecastUpdating } from '../../../components/forecast-updating';
import { getFlatLayout, getSelectOneLayout } from './utils';
import { ProfitLossPredictionsPageWrapper } from '../../profit-loss-predictions-page-wrapper';
import { useCategoryOptions } from '../../hooks/useCategoryOptions';
import { useForecastTabViewOptions } from '../../hooks/useForecastTabViewOptions';
import { useOrganisationAccountNames } from '../../hooks/useOrganisationAccountNames/useOrganisationAccountNames';
import { usePredictionsInfoContext } from '../../../context/predictions-info-context';
import { useUKForecastGrid } from '../../hooks/uk/useUKForecastGrid/useUKForecastGrid';
import { useUserGridSettingsContext } from '../../../reports/user-grid-settings/user-grid-settings-context';

interface PredictionsSubTabUKProps {
  tab: ConsolidatedForecastReportTab;
}

const getLayout = (
  trees: Record<string, ForecastTree>,
  tab: ConsolidatedForecastReportTab,
  selectedCategoryId: CATEGORY_NAME_INDEXES,
  isCashFlow: boolean
): UKForecastLayoutItem[] => {
  if (tab.tab_style === 'select-one') {
    return getSelectOneLayout(trees, tab, selectedCategoryId, isCashFlow);
  } else {
    return getFlatLayout(trees, tab, isCashFlow);
  }
};

export const PredictionsSubTabUK = ({ tab }: PredictionsSubTabUKProps) => {
  const accountNameMap = useOrganisationAccountNames(tab);
  const { forecast, forecastReportV2: forecastReport, isLoading, totalCashPredictRow } = usePredictionsInfoContext();
  const { rowHeights } = useUserGridSettingsContext();
  const { categoryOptions, selectedCategoryId } = useCategoryOptions(forecastReport, tab);
  const { isCashFlow, viewOptions } = useForecastTabViewOptions(tab);

  const getLayoutFunc = useMemo(
    () => (trees: Record<string, ForecastTree>) => getLayout(trees, tab, selectedCategoryId, isCashFlow),
    [selectedCategoryId, tab, isCashFlow]
  );

  const { columns, dataGridRows, totalRow } = useUKForecastGrid(
    forecast,
    forecastReport,
    accountNameMap,
    getLayoutFunc
  );

  if (!forecastReport && isLoading) {
    return <UILoader isFull />;
  }

  return (
    <>
      <ProfitLossPredictionsPageWrapper
        reportColumns={columns}
        rowHeights={rowHeights}
        viewOptions={viewOptions}
        accountRows={dataGridRows}
        totalRow={totalRow}
        category={selectedCategoryId as PredictionsAccountCategories}
        categoryOptions={categoryOptions as { name: string; value: PredictionsSubCategories }[]}
        totalCashPredictRow={totalCashPredictRow}
        showDaysToPay
      />
      <ForecastUpdating show={!!isLoading} />
    </>
  );
};
